import React, { useState, useEffect } from 'react';
import proptypes from 'prop-types';
const { arrayOf, bool, number, shape, string } = proptypes;
import {
  Alert,
  Checkbox,
  IconButton,
  Radio,
  RadioGroup,
  SelectionControlLabel,
  Tooltip,
  Typography
} from '@one-thd/sui-atomic-components';
import { Checkmark, InfoFilled } from '@one-thd/sui-icons';
import { usePartsAndServices } from '../PartsAndServicesProvider';
import {
  INSTALL_OPTIONS,
  INSTALL_POPOVER,
  INSTALL_TEXT,
  INSTALL_STANDARD_KIT_TEXT,
  INSTALL_TWO_STEP,
  INSTALL_ITEM_CONFIGURED,
} from '../../constants';
import { getInstallationDetails, isEmptyObject, isSelected } from '../../utils';

export const InstallationRadioBtns = ({ lineItemId, requiredItems, installationOptions }) => {
  const {
    initializeInstallationSelections,
    installationSelections,
    setInstallationSelections,
    toggleInstallationSelection
  } = usePartsAndServices();

  useEffect(() => {
    if (isEmptyObject(installationSelections)) {
      initializeInstallationSelections();
    }
  }, []);

  const {
    hasHookup: hasSelectedHookup,
    hasInstallation: hasSelectedInstallation
  } = getInstallationDetails({ addOns: installationSelections[lineItemId] });
  const defaultRadioValue = (installationSelections[lineItemId]?.length
    && (hasSelectedHookup || hasSelectedInstallation))
    ? INSTALL_OPTIONS.ACCEPT
    : INSTALL_OPTIONS.DECLINE;

  const [radioValue, setRadioValue] = useState(defaultRadioValue);
  const [anchorPopover, setAnchorPopover] = useState(null);

  useEffect(() => {
    setRadioValue(defaultRadioValue);
  }, [installationSelections]);

  const { hasTwoStepInstallation } = installationOptions;
  const installItem = requiredItems.find(({ installation }) => installation);
  const installPartItems = requiredItems.filter(({ installation }) => !installation);
  const installationPrice = Number.parseFloat((installItem?.price || 0).toFixed(2));
  const standardKitPrice = Number.parseFloat(
    installPartItems.reduce((total, item) => total + item.price, 0).toFixed(2));
  const totalInstallPrice = Number.parseFloat((installationPrice + standardKitPrice).toFixed(2));
  const isPlural = !!(installPartItems.length > 1);

  const onRadioChange = (event) => {
    const selectedValue = event.target.value;
    setRadioValue(selectedValue);

    if (selectedValue === INSTALL_OPTIONS.ACCEPT) {
      const nextSelections = (requiredItems || []).filter(
        (item) => item?.configAttr !== INSTALL_ITEM_CONFIGURED.configAttr
      );
      setInstallationSelections(lineItemId, ...nextSelections);
      return;
    }
    if (selectedValue === INSTALL_OPTIONS.DECLINE) {
      const nextSelections = [INSTALL_ITEM_CONFIGURED, ...installPartItems];
      setInstallationSelections(lineItemId, ...nextSelections);
    }
  };

  const onCheckboxChange = (event) => {
    toggleInstallationSelection(lineItemId, installPartItems[event.target.value]);
  };

  const handlePopoverOpen = (event) => {
    setAnchorPopover(event.currentTarget);
  };

  const popoverId = anchorPopover ? 'top-end-popover' : undefined;

  if (!requiredItems || !requiredItems.length) return null;

  return (
    <>
      {hasTwoStepInstallation && <Alert status="info">{INSTALL_TWO_STEP}</Alert>}
      <Typography variant="body-base" weight="bold" role="heading">
        Installation
      </Typography>
      <div className="sui-mt-1">
        <RadioGroup
          aria-labelledby="installation-radio-buttons"
          name="installation-radio-buttons-group"
          id="install-appliance-yes"
          value={radioValue}
          onChange={onRadioChange}
          alignment="top"
        >
          <SelectionControlLabel
            value="install-accept"
            label={(
              <span className="sui-flex sui-flex-wrap">
                <Typography component="span">Yes, have it up and running for me.&nbsp;</Typography>
                {totalInstallPrice > 0 && (
                  <Typography component="span" weight="bold">${totalInstallPrice.toFixed(2)}</Typography>
                )}
              </span>
            )}
            alignment="top"
          >
            <Radio />
          </SelectionControlLabel>
          {radioValue === INSTALL_OPTIONS.ACCEPT && (
            <div className="sui-pl-8 sui-pb-2 sui-flex sui-flex-col sui-gap-3">
              {!!installItem && (
                <span className="sui-flex sui-gap-1">
                  <span className="sui-flex sui-items-center">
                    <Checkmark size="small" />
                    <span className="sui-pl-2 sui-flex">
                      <Typography
                        component="span"
                        role="note"
                        aria-label={INSTALL_TEXT}
                      >
                        {INSTALL_TEXT}
                      </Typography>
                    </span>
                  </span>
                  <Typography
                    component="span"
                    weight="bold"
                    color={installationPrice > 0 ? 'black' : 'success'}
                    role="note"
                    aria-label={installationPrice > 0 ? `$${installationPrice.toFixed(2)}` : 'FREE'}
                  >
                    {installationPrice > 0 ? `$${installationPrice.toFixed(2)}` : 'FREE'}
                  </Typography>
                </span>
              )}
              {standardKitPrice !== 0 && (
                <span className="sui-flex sui-gap-1 sui-items-start">
                  <Checkmark size="small" />
                  <span className="sui-flex sui-flex-wrap sui--mt-1 sui-gap-1 sui-pl-1">
                    <Typography
                      component="span"
                      role="note"
                      aria-label={INSTALL_STANDARD_KIT_TEXT}
                    >
                      {INSTALL_STANDARD_KIT_TEXT}
                    </Typography>
                    <Typography
                      component="span"
                      weight="bold"
                      role="note"
                      aria-label={`$${standardKitPrice}`}
                    >
                      ${standardKitPrice}
                    </Typography>
                  </span>
                </span>
              )}
            </div>
          )}
          <SelectionControlLabel value={INSTALL_OPTIONS.DECLINE} label="No, I will install it myself.">
            <Radio />
          </SelectionControlLabel>
          {(radioValue === INSTALL_OPTIONS.DECLINE && !!installPartItems.length) && (
            <div className="sui-mt-2 sui-pl-10">
              <span className="sui-flex sui-items-center sui-gap-1">
                <Typography component="span">
                  {`Required Part${installPartItems.length > 1 ? 's' : ''} for Installation`}
                </Typography>
                <Tooltip placement="right" title={INSTALL_POPOVER.TEXT({ isPlural })}>
                  <IconButton
                    aria-describedby={popoverId}
                    onClick={handlePopoverOpen}
                    icon={InfoFilled}
                    iconSize="small"
                    color="info"
                    buttonSize="tight"
                    aria-label="info-button"
                  />
                </Tooltip>
              </span>
              {installPartItems?.map((installPartItem, index) => {
                const { description, price, configAttr } = installPartItem || {};
                return (
                  <div className="sui-mt-1" key={configAttr}>
                    <SelectionControlLabel
                      alignment="top"
                      key={configAttr}
                      value={description}
                      label={(
                        <span className="sui-flex sui-gap-1 sui-flex-wrap sui-mt-1">
                          <Typography component="span">{description}</Typography>
                          <Typography component="span" weight="bold">${price}</Typography>
                        </span>
                      )}
                    >
                      <Checkbox
                        checked={isSelected({ selections: installationSelections, lineItemId, configAttr })}
                        value={String(index)}
                        name={description}
                        onChange={onCheckboxChange}
                      />
                    </SelectionControlLabel>
                  </div>
                );
              })}
            </div>
          )}
        </RadioGroup>
      </div>
    </>
  );
};

InstallationRadioBtns.propTypes = {
  lineItemId: string.isRequired,
  requiredItems: arrayOf(shape({
    description: string,
    installation: bool,
    price: number,
    configAttr: string,
    termLength: number,
    category: string
  })),
  installationOptions: shape({
    hasInstallation: bool,
    hasHookup: bool,
    hasTwoStepInstallation: bool
  }).isRequired
};

InstallationRadioBtns.defaultProps = {
  requiredItems: []
};

InstallationRadioBtns.displayName = 'InstallationRadioBtns';
